import { createFileRoute } from "@tanstack/react-router";
import { PageHero } from "@/components/site/Layout";
import { EventEmptyState } from "@/components/site/EventEmptyState";
import { api } from "@/lib/api";
import { useSelectedEvent } from "@/lib/event-context";
import { eventLabel, resolveEventImageUrl } from "@/lib/events";
import { useState, useEffect } from "react";

interface AboutContent {
  id: number;
  content_type: "paragraph" | "stat";
  heading: string | null;
  subtitle: string | null;
  badge_text: string | null;
  section_heading: string | null;
  content: string | null;
  stat_value: string | null;
  stat_label: string | null;
  sort_order: number;
  image_url: string | null;
}

export const Route = createFileRoute("/about")({
  component: About,
  head: () => ({
    meta: [
      { title: "About — Arab Capital Markets Conference" },
      {
        name: "description",
        content: "About the Third Arab Capital Markets Conference held in Doha, Qatar.",
      },
      { property: "og:title", content: "About the Conference" },
      {
        property: "og:description",
        content: "Convening Arab capital markets leadership in Doha, 2024.",
      },
    ],
  }),
});

function About() {
  const { selectedEvent, loading: eventsLoading } = useSelectedEvent();
  const [aboutContent, setAboutContent] = useState<AboutContent[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadAboutContent();
  }, []);

  const loadAboutContent = async () => {
    try {
      const response = await api.get<AboutContent[]>("/api/about");
      if (response.success && response.data) {
        setAboutContent(response.data);
      }
    } catch (error) {
      console.error("Failed to load about content:", error);
    } finally {
      setLoading(false);
    }
  };

  const paragraphs = aboutContent
    .filter((item) => item.content_type === "paragraph" && item.content)
    .sort((a, b) => a.sort_order - b.sort_order);

  const stats = aboutContent
    .filter((item) => item.content_type === "stat" && item.stat_value && item.stat_label)
    .sort((a, b) => a.sort_order - b.sort_order);

  const firstParagraph = paragraphs[0];
  const heading = selectedEvent?.title || firstParagraph?.heading || "About the Event";
  const subtitle = selectedEvent ? eventLabel(selectedEvent) : firstParagraph?.subtitle || "";
  const badgeText = firstParagraph?.badge_text || "About the Conference";
  const sectionHeading = selectedEvent?.subtitle || firstParagraph?.section_heading || "About this event";
  const imageUrl = selectedEvent?.image_url
    ? resolveEventImageUrl(selectedEvent.image_url)
    : firstParagraph?.image_url
      ? resolveEventImageUrl(firstParagraph.image_url)
      : null;
  const eventDescription = selectedEvent?.description?.trim();

  if (loading || eventsLoading) {
    return (
      <>
        <PageHero
          eyebrow="About"
          title={heading}
          subtitle="The Third Arab Capital Markets Conference — Doha, 25 April 2024"
        />
        <section className="py-12 sm:py-16 md:py-8 px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center text-muted-foreground">Loading...</div>
        </section>
      </>
    );
  }

  if (!selectedEvent) {
    return (
      <>
        <PageHero eyebrow="About" title="About the Event" />
        <EventEmptyState title="Choose an event" message="Select an event in the header to read about that conference." />
      </>
    );
  }

  return (
    <>
      <PageHero
        eyebrow="About"
        title={heading}
        subtitle={subtitle}
      />
      <section className="py-16 sm:py-20 md:py-8 px-4 sm:px-6 lg:px-8 bg-background">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            {/* Image Section */}
            {imageUrl && (
              <div className="order-2 lg:order-1">
                <div className="relative">
                  <div className="absolute -inset-4 bg-gradient-to-br from-accent/20 to-primary/20 rounded-3xl blur-2xl" />
                  <div className="relative rounded-3xl overflow-hidden shadow-2xl">
                    <img
                      src={imageUrl}
                      alt="About the conference"
                      className="w-full h-auto object-cover"
                      onError={(e) => {
                        console.error('Image failed to load:', imageUrl);
                        console.error('Error event:', e);
                        (e.target as HTMLImageElement).style.display = 'none';
                      }}
                      onLoad={() => {
                        console.log('Image loaded successfully:', imageUrl);
                      }}
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Content Section */}
            <div className={`${imageUrl ? 'order-1 lg:order-2' : 'order-1 lg:col-span-2'}`}>
              {badgeText && (
                <div className="inline-block px-3 py-1 rounded-full bg-accent/10 text-accent text-[10px] sm:text-xs uppercase tracking-[0.3em] mb-4 sm:mb-6">
                  {badgeText}
                </div>
              )}
              {sectionHeading && (
                <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl text-primary mb-6 sm:mb-8 leading-tight">
                  {sectionHeading}
                </h2>
              )}
              <div className="space-y-5 sm:space-y-6 text-muted-foreground leading-relaxed text-base md:text-lg">
                {eventDescription ? (
                  <div
                    className="prose prose-base max-w-none"
                    dangerouslySetInnerHTML={{ __html: eventDescription }}
                  />
                ) : paragraphs.map((item) => (
                  <div
                    key={item.id}
                    className="prose prose-base max-w-none"
                    dangerouslySetInnerHTML={{ __html: item.content }}
                  />
                ))}
              </div>
            </div>
          </div>

          {/* Stats Section */}
          {stats.length > 0 && (
            <div className="mt-16 sm:mt-20 lg:mt-24">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-6 lg:gap-8">
                {stats.map((item, index) => (
                  <div
                    key={item.id}
                    className="p-6 sm:p-8 lg:p-10 rounded-2xl bg-gradient-to-br from-secondary to-secondary/50 border border-border shadow-lg hover:shadow-xl transition-shadow"
                  >
                    <div className="font-serif text-4xl sm:text-5xl lg:text-6xl text-primary mb-2 sm:mb-3">
                      {item.stat_value}
                    </div>
                    <div className="text-xs sm:text-sm uppercase tracking-widest text-muted-foreground">
                      {item.stat_label}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </section>
    </>
  );
}
